// frontend/src/components/shared/UISimpleTable/UISimpleTable.tsx
import React from 'react';
import { ReactComponent as SortArrowUpIcon } from '@/img/sort-arrow-up.svg';
import { ReactComponent as SortArrowDownIcon } from '@/img/sort-arrow-down.svg';
import { ReactComponent as SeparatorDashedIcon } from '@/img/separator-dashed.svg';
import './UISimpleTable.scss';

export interface SimpleTableColumn {
  key: string;
  label: string;
  width?: string;
  render: (data: any, row: any, index: number) => React.ReactNode;
  headerContent?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  showDivider?: boolean;
}

export interface UISimpleTableProps {
  data: any[];
  columns: SimpleTableColumn[];
  className?: string;
  getRowKey?: (row: any, index: number) => string | number;
  onSort?: (columnKey: string, direction: 'asc' | 'desc') => void;
  onRowClick?: (row: any, index: number) => void;
}

// Моковые данные для демонстрації
const mockData = [
  {
    id: 1,
    name: 'Building A',
    address: { street: '123 Main St', city: 'Kyiv' },
    units: [1, 2, 3],
    status: 'occupied',
  },
  {
    id: 2,
    name: 'Building B',
    address: { street: '456 Oak Ave', city: 'Lviv' },
    units: [1, 2],
    status: 'vacant',
  },
];

const mockColumns: SimpleTableColumn[] = [
  {
    key: 'name',
    label: 'Property',
    width: '140px',
    sortable: true,
    sortDirection: null,
    showDivider: true,
    render: (value: string, row: any) => (
      <div>
        <div>{row.name}</div>
        <div
          style={{
            fontSize: '10px',
            background: '#76C102',
            color: 'white',
            padding: '2px 4px',
            borderRadius: '2px',
            display: 'inline-block',
          }}
        >
          {row.status}
        </div>
      </div>
    ),
  },
  {
    key: 'address',
    label: 'Address',
    width: '231px',
    sortable: true,
    sortDirection: null,
    showDivider: true,
    render: (value: any, row: any) => (
      <div>
        {row.address?.street}, {row.address?.city}
      </div>
    ),
  },
  {
    key: 'units',
    label: 'Units',
    width: '89px',
    sortable: true,
    sortDirection: null,
    render: (value: any, row: any) => <div>{row.units?.length || 0}</div>,
  },
];

const baseClass = 'UISimpleTable';

const UISimpleTable: React.FC<UISimpleTableProps> = ({
  data = mockData,
  columns = mockColumns,
  className = '',
  getRowKey = (row, index) => row.id || index,
  onSort,
  onRowClick,
}) => {
  const tableClasses = [baseClass, className].filter(Boolean).join(' ');

  const handleSort = (columnKey: string, direction: 'asc' | 'desc') => {
    if (!onSort) return;
    onSort(columnKey, direction);
  };

  const handleRowClick = (row: any, index: number) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  const renderSortIcon = (column: SimpleTableColumn) => {
    if (!column.sortable) return null;

    const handleArrowHover = (element: SVGElement, isHover: boolean) => {
      if (!element.classList.contains(`${baseClass}__sort-arrow--active`)) {
        element.style.fill = isHover ? '#105E96' : '#C4CDD5';
      }
    };

    return (
      <div className={`${baseClass}__sort-container`}>
        <div className={`${baseClass}__sort-arrows`}>
          <SortArrowUpIcon
            className={`${baseClass}__sort-arrow ${baseClass}__sort-arrow--up ${
              column.sortDirection === 'asc'
                ? `${baseClass}__sort-arrow--active`
                : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleSort(column.key, 'asc');
            }}
            onMouseEnter={(e) => handleArrowHover(e.currentTarget, true)}
            onMouseLeave={(e) => handleArrowHover(e.currentTarget, false)}
          />
          <SortArrowDownIcon
            className={`${baseClass}__sort-arrow ${baseClass}__sort-arrow--down ${
              column.sortDirection === 'desc'
                ? `${baseClass}__sort-arrow--active`
                : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleSort(column.key, 'desc');
            }}
            onMouseEnter={(e) => handleArrowHover(e.currentTarget, true)}
            onMouseLeave={(e) => handleArrowHover(e.currentTarget, false)}
          />
        </div>
      </div>
    );
  };

  const renderDivider = () => (
    <div className={`${baseClass}__header-divider`}>
      <SeparatorDashedIcon />
    </div>
  );

  return (
    <div className={tableClasses}>
      <div className={`${baseClass}__table`}>
        {/* Заголовок таблиці */}
        <div className={`${baseClass}__header`}>
          <div className={`${baseClass}__header-row`}>
            {columns.map((column, index) => (
              <div
                key={column.key}
                className={`${baseClass}__header-cell ${baseClass}__header-cell--${column.key} ${column.headerClassName || ''}`}
                style={{ width: column.width }}
              >
                <div className={`${baseClass}__header-content`}>
                  <div className={`${baseClass}__header-label`}>
                    {column.headerContent || <span>{column.label}</span>}
                  </div>
                  {renderSortIcon(column)}
                </div>
                {column.showDivider && renderDivider()}
              </div>
            ))}
          </div>
        </div>

        {/* Тіло таблиці */}
        <div className={`${baseClass}__body`}>
          {data.map((row, index) => (
            <div
              key={getRowKey(row, index)}
              className={`${baseClass}__row ${onRowClick ? 'clickable' : ''}`}
              onClick={() => handleRowClick(row, index)}
            >
              {columns.map((column) => (
                <div
                  key={column.key}
                  className={`${baseClass}__cell ${baseClass}__cell--${column.key} ${column.className || ''}`}
                  style={{ width: column.width }}
                  data-label={column.label}
                >
                  {column.render(row[column.key], row, index)}
                </div>
              ))}
            </div>
          ))}
        </div>


      </div>
    </div>
  );
};

export default UISimpleTable;
